import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import { toast } from "sonner";
import {
  TrendingUp, Users, Mail, DollarSign, Activity, Sparkles,
} from "lucide-react";

const RANGES = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

const TIER_COLORS = {
  free: "#6B7280",
  workshop: "#FF8A00",
  course: "#F97316",
  membership: "#E52E71",
  cohort: "#A855F7",
};

export default function AnalyticsAdminTab() {
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/analytics/deep?days=${range}`);
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load analytics.");
      } finally {
        setLoading(false);
      }
    })();
  }, [range]);

  if (loading || !data) return <div className="text-white/50">Loading analytics…</div>;

  const t = data.totals;
  const money = (cents) => `$${(cents / 100).toFixed(0)}`;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Header + range selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-white/85">
            <Activity size={18} className="text-[#FF8A00]" />
            <span className="font-display text-xl">Analytics</span>
          </div>
          <p className="text-sm text-white/50 mt-1">
            {data.start_date} → {data.end_date} · updated live
          </p>
        </div>
        <div className="inline-flex bg-white/5 rounded-full p-1 border border-white/10">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.label}`}
              className={`px-4 py-1.5 rounded-full text-xs tracking-wider transition ${
                range === r.key
                  ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white"
                  : "text-white/60 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="analytics-kpis">
        <Kpi icon={Mail}   label="Subscribers"    value={t.subscribers_window}   sub={`in last ${range}d`} />
        <Kpi icon={Users}  label="New users"      value={t.user_signups_window}  sub={`in last ${range}d`} />
        <Kpi icon={DollarSign} label="Revenue"    value={money(t.revenue_cents_window)} sub={`in last ${range}d`} />
        <Kpi icon={Activity}   label="Active 7d"  value={`${t.active_7d}`} sub={`${t.active_7d_pct}% of users`} />
        <Kpi icon={TrendingUp} label="Active 30d" value={`${t.active_30d}`} sub={`${t.active_30d_pct}% of users`} />
      </div>

      {/* Subscriber growth chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Subscriber growth" subtitle="Newsletter signups per day">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.subscriber_growth}>
              <defs>
                <linearGradient id="subs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF8A00" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#FF8A00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.35)" fontSize={10}
                     tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="rgba(255,255,255,0.35)" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.08)" }} />
              <Area type="monotone" dataKey="signups" stroke="#FF8A00" strokeWidth={2} fill="url(#subs)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue trend" subtitle={`${money(t.revenue_cents_window)} in the window`}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.revenue_series}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.35)" fontSize={10}
                     tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="rgba(255,255,255,0.35)" fontSize={10}
                     tickFormatter={(v) => `$${Math.round(v / 100)}`} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.08)" }}
                formatter={(v) => [`$${(v / 100).toFixed(0)}`, "Revenue"]}
              />
              <Line type="monotone" dataKey="revenue_cents" stroke="#E52E71" strokeWidth={2}
                    dot={{ r: 2, fill: "#E52E71" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Tier funnel */}
      <ChartCard
        title="Tier conversion funnel"
        subtitle="Users at or above each tier, plus % conversion vs the tier above"
        testid="analytics-funnel"
      >
        <div className="space-y-3 mt-3">
          {data.tier_funnel.map((row, i) => {
            const color = TIER_COLORS[row.tier] || "#FF8A00";
            const maxWidth = data.tier_funnel[0]?.at_or_above || 1;
            const barPct = (row.at_or_above / maxWidth) * 100;
            return (
              <div key={row.tier} data-testid={`funnel-${row.tier}`}
                   className="flex items-center gap-4">
                <div className="w-28 shrink-0 text-xs uppercase tracking-wider text-white/70">
                  {row.tier}
                </div>
                <div className="flex-1 relative h-9 bg-white/5 rounded-lg overflow-hidden">
                  <div
                    className="h-full rounded-lg flex items-center px-3 text-xs font-medium text-white transition-all"
                    style={{ width: `${Math.max(barPct, 5)}%`, background: `linear-gradient(90deg, ${color}, ${color}80)` }}
                  >
                    {row.at_or_above}
                  </div>
                </div>
                <div className="w-24 shrink-0 text-right text-xs">
                  <div className="text-white/85">{row.count} <span className="text-white/40">at</span></div>
                  {i > 0 && (
                    <div className="text-[10px] text-[#FF8A00] mt-0.5 tracking-wider">
                      {row.conversion_pct}% conv
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ChartCard>

      {/* User growth */}
      <ChartCard title="User signups" subtitle="New accounts registered per day">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.user_growth}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="date" stroke="rgba(255,255,255,0.35)" fontSize={10}
                   tickFormatter={(v) => v.slice(5)} />
            <YAxis stroke="rgba(255,255,255,0.35)" fontSize={10} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.08)" }} />
            <Bar dataKey="signups" radius={[4, 4, 0, 0]}>
              {data.user_growth.map((_, i) => (
                <Cell key={i} fill="#FF8A00" fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="glass rounded-2xl p-4 border border-white/8">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">{label}</div>
        <Icon size={14} className="text-[#FF8A00]" />
      </div>
      <div className="font-display text-3xl mt-2">{value}</div>
      <div className="text-[11px] text-white/40 mt-1">{sub}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="glass rounded-2xl p-5 border border-white/8" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-white/85 font-medium">{title}</div>
          {subtitle && <div className="text-xs text-white/45 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}
